import { useCallback } from "react";
import { useAtomValue } from "jotai";
import { trackEvent } from "common/analytics";
import { mapViewportAtom } from "@/state/atoms";
import { useSearchPageParams } from "common/hooks/business/home/useSearchPageParams";

// "sociability" = places with data (clustered). "overview" = all venues as
// dots at low zoom, pins at high zoom, never clustered. "declutter" = same
// venues as individual pins, decluttered by zoom (no clustering). "all" =
// every venue + native Google POIs.
export type MapMode = "sociability" | "overview" | "declutter" | "all";

/**
 * Shared map-mode state: derives the current mode from search params and exposes
 * a setter that also pins the search to the live map viewport. Used by both the
 * list header toggle (desktop) and the on-map toggle (mobile).
 */
export function useMapMode() {
  const { searchPageParams, setSearchPageParams } = useSearchPageParams();
  const mapViewport = useAtomValue(mapViewportAtom);
  const mapMode: MapMode =
    searchPageParams.map_mode === "all"
      ? "all"
      : searchPageParams.map_mode === "declutter"
        ? "declutter"
        : searchPageParams.map_mode === "overview"
          ? "overview"
          : "sociability";

  const changeMapMode = useCallback(
    (mode: MapMode) => {
      const vp = mapViewport;
      // Real zoom + explicit area search (like "Show nearby"), so results load
      // immediately using the live viewport bounds.
      const viewportParams = vp
        ? {
            latitude: vp.lat.toString(),
            longitude: vp.lng.toString(),
            zoom: vp.zoom.toString(),
            searchThisAreaClicked: "yes",
            autoZoom: "no",
            map_ne_lat: vp.ne.lat.toString(),
            map_ne_lng: vp.ne.lng.toString(),
            map_sw_lat: vp.sw.lat.toString(),
            map_sw_lng: vp.sw.lng.toString(),
          }
        : {};
      setSearchPageParams((prev) => ({
        ...prev,
        ...viewportParams,
        map_mode: mode,
        // sociability/declutter/overview query mapped venues only; "all"
        // drops the filter to include unmapped ones too.
        mapping_status: mode === "all" ? undefined : "mapped",
        section_filter: undefined,
        tagging_mode: "off",
        unixTimestamp: Math.floor(Date.now() / 1000),
      }));

      // search_results_pin_view_changed — the "All places" <-> "Places with data" map
      // pin toggle. Scoped to the search results page (the event's page) and only when
      // the value actually changes. A results-wide toggle has no single place, so the
      // place dimensions are "" (mapper/user is the sibling toggle in VenueList).
      if (
        mode !== mapMode &&
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/search")
      ) {
        trackEvent("search_results_pin_view_changed", {
          brand_id: "",
          brand_name: "",
          business_id: "",
          place_name: "",
          // sociability -> with_data; declutter -> without_data; all -> all;
          // overview -> overview.
          pin_data_type:
            mode === "all"
              ? "all"
              : mode === "declutter"
                ? "without_data"
                : mode === "overview"
                  ? "overview"
                  : "with_data",
          previous_pin_data_type:
            mapMode === "all"
              ? "all"
              : mapMode === "declutter"
                ? "without_data"
                : mapMode === "overview"
                  ? "overview"
                  : "with_data",
        });
      }
    },
    [setSearchPageParams, mapViewport, mapMode],
  );

  return { mapMode, changeMapMode };
}
